import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Popover } from 'antd';
import { SettingOutlined, CheckOutlined } from '@ant-design/icons';
import { Building, Search, CheckLg, ArrowUp, ArrowDown, XLg } from 'react-bootstrap-icons';
import { setSelectedCompany } from '@stores/reducers/company.reducer';
import { formatDocument } from '@helpers/FormatDocument';
import { getIcon } from './topbarIconMap';

export default function TopbarConfig({
  availableScreens,
  selectedKeys,
  toggleShortcut,
  saveShortcuts,
  maxShortcuts,
}) {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const companies = useSelector(state => state.company.companies);
  const companiesUser = useSelector(state => state.company.companiesUser);
  const selectedCompany = useSelector(state => state.company.selectedCompany);

  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState('shortcuts');
  const [companySearch, setCompanySearch] = useState('');
  const [tempSelection, setTempSelection] = useState([]);

  const availableCompanies = useMemo(() => {
    return user?.isAdmin ? companies : companiesUser;
  }, [user, companies, companiesUser]);

  const filteredCompanies = useMemo(() => {
    if (!companySearch) return availableCompanies;
    const term = companySearch.toLowerCase();
    return availableCompanies.filter(
      c =>
        c.name?.toLowerCase().includes(term) ||
        c.document?.includes(term) ||
        c.short_name?.toLowerCase().includes(term)
    );
  }, [availableCompanies, companySearch]);

  const hasMultipleCompanies = availableCompanies.length > 1;

  const handleOpen = (v) => {
    if (v) {
      setTempSelection([...selectedKeys]);
      setTab('shortcuts');
      setCompanySearch('');
    }
    setVisible(v);
  };

  const handleSelectCompany = (company) => {
    dispatch(setSelectedCompany(company));
    setTab('shortcuts');
    setCompanySearch('');
  };

  const handleToggleTemp = (key) => {
    setTempSelection(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      }
      if (prev.length >= maxShortcuts) return prev;
      return [...prev, key];
    });
  };

  const handleMoveUp = (key) => {
    setTempSelection(prev => {
      const idx = prev.indexOf(key);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const handleMoveDown = (key) => {
    setTempSelection(prev => {
      const idx = prev.indexOf(key);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handleRemove = (key) => {
    setTempSelection(prev => prev.filter(k => k !== key));
  };

  const handleSave = () => {
    saveShortcuts(tempSelection);
    setVisible(false);
  };

  const groupedScreens = useMemo(() => {
    const groups = {};
    availableScreens.forEach(screen => {
      if (!groups[screen.menuLabel]) {
        groups[screen.menuLabel] = [];
      }
      groups[screen.menuLabel].push(screen);
    });
    return groups;
  }, [availableScreens]);

  const selectedScreens = useMemo(() => {
    return tempSelection
      .map(key => availableScreens.find(s => s.key === key))
      .filter(Boolean);
  }, [tempSelection, availableScreens]);

  const content = (
    <div style={{ width: 400, maxHeight: 520, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
        <button
          onClick={() => setTab('shortcuts')}
          style={{
            flex: 1,
            padding: '12px 0',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: tab === 'shortcuts' ? 600 : 400,
            color: tab === 'shortcuts' ? '#3b82f6' : '#64748b',
            borderBottom: tab === 'shortcuts' ? '2px solid #3b82f6' : '2px solid transparent',
          }}
        >
          Atalhos
        </button>
        {selectedCompany && (
          <button
            onClick={() => setTab('company')}
            style={{
              flex: 1,
              padding: '12px 0',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: tab === 'company' ? 600 : 400,
              color: tab === 'company' ? '#3b82f6' : '#64748b',
              borderBottom: tab === 'company' ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            Empresa
          </button>
        )}
      </div>

      {tab === 'shortcuts' && (
        <>
          {selectedScreens.length > 0 && (
            <div style={{ borderBottom: '1px solid #f0f0f0' }}>
              <div
                style={{
                  padding: '8px 16px 4px',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#3b82f6',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>Selecionados ({selectedScreens.length}/{maxShortcuts})</span>
                <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10, textTransform: 'none' }}>
                  Use as setas para ordenar
                </span>
              </div>
              {selectedScreens.map((screen, idx) => {
                const Icon = getIcon(screen.icon);
                return (
                  <div
                    key={screen.key}
                    style={{
                      padding: '6px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      backgroundColor: '#f0f7ff',
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </span>
                    <Icon size={14} style={{ color: '#3b82f6', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {screen.label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                      <button
                        onClick={() => handleMoveUp(screen.key)}
                        disabled={idx === 0}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: idx === 0 ? 'default' : 'pointer',
                          padding: 2,
                          color: idx === 0 ? '#cbd5e1' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => handleMoveDown(screen.key)}
                        disabled={idx === selectedScreens.length - 1}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: idx === selectedScreens.length - 1 ? 'default' : 'pointer',
                          padding: 2,
                          color: idx === selectedScreens.length - 1 ? '#cbd5e1' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <ArrowDown size={12} />
                      </button>
                      <button
                        onClick={() => handleRemove(screen.key)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 2,
                          color: '#ef4444',
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: 2,
                        }}
                      >
                        <XLg size={10} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ padding: '8px 16px 4px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Telas disponíveis
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 260 }}>
            {Object.entries(groupedScreens).map(([groupLabel, screens]) => {
              const GIcon = getIcon(screens[0]?.icon);
              return (
                <div key={groupLabel}>
                  <div
                    style={{
                      padding: '8px 16px 4px',
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <GIcon size={11} style={{ opacity: 0.6 }} />
                    {groupLabel}
                  </div>
                  {screens.map(screen => {
                    const isChecked = tempSelection.includes(screen.key);
                    const isDisabled = !isChecked && tempSelection.length >= maxShortcuts;
                    const Icon = getIcon(screen.icon);
                    return (
                      <div
                        key={screen.key}
                        onClick={() => !isDisabled && handleToggleTemp(screen.key)}
                        style={{
                          padding: '7px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.35 : 1,
                          backgroundColor: isChecked ? '#f0f7ff' : 'transparent',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={e => {
                          if (!isDisabled && !isChecked) e.currentTarget.style.backgroundColor = '#f8fafc';
                        }}
                        onMouseLeave={e => {
                          if (!isChecked) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Icon size={14} style={{ color: isChecked ? '#3b82f6' : '#94a3b8', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: '#1e293b', flex: 1 }}>{screen.label}</span>
                        {isChecked && <CheckOutlined style={{ color: '#3b82f6', fontSize: 12 }} />}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {availableScreens.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                Nenhuma tela disponível
              </div>
            )}
          </div>
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
            }}
          >
            <button
              onClick={() => setVisible(false)}
              style={{
                padding: '6px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
                fontSize: 12,
                color: '#64748b',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '6px 16px',
                border: 'none',
                borderRadius: 6,
                background: '#3b82f6',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Salvar
            </button>
          </div>
        </>
      )}

      {tab === 'company' && (
        <>
          {selectedCompany && (
            <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Building style={{ color: '#fff', fontSize: 18 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#1e293b',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedCompany.name}
                  </div>
                  {selectedCompany.document && (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      {formatDocument(selectedCompany.document)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {hasMultipleCompanies && (
            <>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ position: 'relative' }}>
                  <Search
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#999',
                      fontSize: 14,
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Buscar empresa..."
                    value={companySearch}
                    onChange={e => setCompanySearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px 8px 32px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                </div>
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {filteredCompanies.map(company => {
                  const isSelected = selectedCompany?.id === company.id;
                  return (
                    <div
                      key={company.id}
                      onClick={() => handleSelectCompany(company)}
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        backgroundColor: isSelected ? '#f0f7ff' : 'transparent',
                        borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={e => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onMouseLeave={e => {
                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          backgroundColor: isSelected ? '#3b82f6' : '#e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Building style={{ color: isSelected ? '#fff' : '#64748b', fontSize: 14 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: isSelected ? 600 : 400,
                            color: '#1e293b',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {company.name}
                        </div>
                        {company.document && (
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {formatDocument(company.document)}
                          </div>
                        )}
                      </div>
                      {isSelected && <CheckLg style={{ color: '#3b82f6', fontSize: 16, flexShrink: 0 }} />}
                    </div>
                  );
                })}
                {filteredCompanies.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
                    Nenhuma empresa encontrada
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={visible}
      onOpenChange={handleOpen}
      placement="bottomLeft"
      overlayStyle={{ padding: 0 }}
      overlayInnerStyle={{ padding: 0, borderRadius: 10, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 4,
        }}
      >
        <SettingOutlined style={{ fontSize: 20, color: '#94a3b8' }} />
      </div>
    </Popover>
  );
}
